/**
 * Fuente única de precios Stripe por catalogId (live + test). Todo lo demás — el web
 * (apps/web/src/content/{products,services,treatments}.ts, vía catalogPriceId en
 * lib/utils.ts) y el allowlist del API (stripe-prices.ts) — deriva de aquí. No dupliques
 * un priceId a mano en otro archivo: agrégalo/cámbialo solo en CATALOG_ITEMS.
 */

export type CatalogItemKind = "product" | "service" | "treatment"

export interface CatalogPriceEntry {
	catalogId: string
	name: string
	kind: CatalogItemKind
	livePriceId: string
	testPriceId: string
}

/** Único price de test para todos los tratamientos (preview/local). */
export const TREATMENT_TEST_STRIPE_PRICE_ID = "price_1N9BD4BDAG09O533GA1PyACp"

/** Catálogo de ítems promocionables / agregables. */
export const CATALOG_ITEMS: readonly CatalogPriceEntry[] = [
	{
		catalogId: "shalosh-bayom",
		name: "Shalósh Bayóm",
		kind: "product",
		livePriceId: "price_1MdHv1BDAG09O533x9m8D95e",
		testPriceId: "price_1MbsPsBDAG09O533D3JE6rsf"
	},
	{
		catalogId: "dermogel",
		name: "DermoGel",
		kind: "product",
		livePriceId: "price_1MdHzMBDAG09O533TTbcnm4F",
		testPriceId: "price_1MbsVlBDAG09O5330IgaDBYl"
	},
	{
		catalogId: "taftefet",
		name: "Taftéfet",
		kind: "product",
		livePriceId: "price_1MdI2HBDAG09O533A1E1oIH3",
		testPriceId: "price_1MoBQ3BDAG09O533kbuN5Wc9"
	},
	{
		catalogId: "agua-vita",
		name: "Agua Vita",
		kind: "product",
		livePriceId: "price_1MdI0bBDAG09O533iLpjHXpO",
		testPriceId: "price_1MbsczBDAG09O533VQqQFNZM"
	},
	{
		catalogId: "lazihum",
		name: "Lazihúm",
		kind: "product",
		livePriceId: "price_1MdI43BDAG09O5335qhCPBuw",
		testPriceId: "price_1MoBfIBDAG09O533538dHAEc"
	},
	{
		catalogId: "nazelet",
		name: "Nazélet",
		kind: "product",
		livePriceId: "price_1MdI63BDAG09O533uPAGvLbV",
		testPriceId: "price_1MoBg0BDAG09O533iE5InPT0"
	},
	{
		catalogId: "jabon-neutro",
		name: "Jabón Neutro",
		kind: "product",
		livePriceId: "price_1MdI83BDAG09O533WsHY58GK",
		testPriceId: "price_1MoBh6BDAG09O533OPU80PRM"
	},
	{
		catalogId: "te",
		name: "Té",
		kind: "product",
		livePriceId: "price_1MdfV1BDAG09O533ZxFtBep0",
		testPriceId: "price_1O9D8aBDAG09O533S93kEth5"
	},
	{
		catalogId: "consulta",
		name: "Consulta remota",
		kind: "service",
		livePriceId: "price_1NQcYyBDAG09O533sOvFAkZx",
		testPriceId: "price_1NQcbuBDAG09O533Rb6MEotR"
	},
	{
		catalogId: "dieta",
		name: "Dieta personalizada",
		kind: "service",
		livePriceId: "price_1MqX9uBDAG09O533VCZfJxaR",
		testPriceId: "price_1NQccpBDAG09O533TXAdOTtj"
	},
	{
		catalogId: "acidez-basico",
		name: "Tratamiento Acidez (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlxSBDAG09O533AMyFei5u",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "acidez-completo",
		name: "Tratamiento Acidez (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlxSBDAG09O533DOqviPxH",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "acne-basico",
		name: "Tratamiento Acné (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlwBBDAG09O533DvEmT3LW",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "acne-completo",
		name: "Tratamiento Acné (Completo)",
		kind: "treatment",
		livePriceId: "price_1NCe45BDAG09O533Gfw0dKKM",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "ansiedad-basico",
		name: "Tratamiento Ansiedad (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvltKBDAG09O533QU4N9gFB",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "ansiedad-completo",
		name: "Tratamiento Ansiedad (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvltKBDAG09O533pVclIVQr",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "artritis-basico",
		name: "Tratamiento Artritis (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlrrBDAG09O533Xg1GU8ZU",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "artritis-completo",
		name: "Tratamiento Artritis (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlrrBDAG09O533zsJgID7B",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "amenorrea-basico",
		name: "Tratamiento Amenorrea (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlumBDAG09O533n2yk8N58",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "amenorrea-completo",
		name: "Tratamiento Amenorrea (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlumBDAG09O533yv7lKQAf",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "asma-basico",
		name: "Tratamiento Asma (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlqSBDAG09O5333wSzxooq",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "asma-completo",
		name: "Tratamiento Asma (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlqSBDAG09O533Bw3u3GUT",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "bronquitis-basico",
		name: "Tratamiento Bronquitis (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlYNBDAG09O533yO1TWbmo",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "bronquitis-completo",
		name: "Tratamiento Bronquitis (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlYwBDAG09O5338m4hE802",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "cancer-basico",
		name: "Tratamiento Cáncer (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlohBDAG09O533HAiyQGYX",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "cancer-completo",
		name: "Tratamiento Cáncer (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlohBDAG09O533Jv6LY5ph",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "cataratas-basico",
		name: "Tratamiento Cataratas (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlnDBDAG09O533gJoAWWHg",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "cataratas-completo",
		name: "Tratamiento Cataratas (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlnDBDAG09O533CTRpCFuc",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "conjuntivitis-basico",
		name: "Tratamiento Conjuntivitis (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvllgBDAG09O5336uNT88ji",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "conjuntivitis-completo",
		name: "Tratamiento Conjuntivitis (Completo)",
		kind: "treatment",
		livePriceId: "price_1N8ftKBDAG09O533qVNRy7xD",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "dermatitis-basico",
		name: "Tratamiento Dermatitis (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvljzBDAG09O533ibUXNjCg",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "dermatitis-completo",
		name: "Tratamiento Dermatitis (Completo)",
		kind: "treatment",
		livePriceId: "price_1OISD9BDAG09O533WdN4k5Pj",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "diabetes-basico",
		name: "Tratamiento Diabetes Tipo 1 (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvliDBDAG09O533Jz3iuMvl",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "diabetes-completo",
		name: "Tratamiento Diabetes Tipo 1 (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvliDBDAG09O533Sw8c22SU",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "diabetes-tipo-2-basico",
		name: "Tratamiento Diabetes Tipo 2 (Básico)",
		kind: "treatment",
		livePriceId: "price_1Ovlg4BDAG09O533Ukp2aVeC",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "diabetes-tipo-2-completo",
		name: "Tratamiento Diabetes Tipo 2 (Completo)",
		kind: "treatment",
		livePriceId: "price_1Ovlg4BDAG09O533eSHjsXEQ",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "envejecimiento-prematuro-basico",
		name: "Tratamiento Envejecimiento Prematuro (Básico)",
		kind: "treatment",
		livePriceId: "price_1Ovm1vBDAG09O533emXISi6f",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "envejecimiento-prematuro-completo",
		name: "Tratamiento Envejecimiento Prematuro (Completo)",
		kind: "treatment",
		livePriceId: "price_1Ovm1vBDAG09O533vk1bl0ef",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "estrenimiento-basico",
		name: "Tratamiento Estreñimiento (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlcdBDAG09O533WiYvNu2B",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "estrenimiento-completo",
		name: "Tratamiento Estreñimiento (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlcdBDAG09O533hF6OWR3L",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "heridas-basico",
		name: "Tratamiento Heridas (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlWfBDAG09O533J6ZaH3Bf",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "heridas-completo",
		name: "Tratamiento Heridas (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlWfBDAG09O533fiOe6VcB",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "menopausia-basico",
		name: "Tratamiento Menopausia (Básico)",
		kind: "treatment",
		livePriceId: "price_1Ovm4NBDAG09O533DnNvrWZ7",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "menopausia-completo",
		name: "Tratamiento Menopausia (Completo)",
		kind: "treatment",
		livePriceId: "price_1Ovm4NBDAG09O5339RuVDkyj",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "parkinson-basico",
		name: "Tratamiento Parkinson (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlTVBDAG09O533wqKW8dWH",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "parkinson-completo",
		name: "Tratamiento Parkinson (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlTVBDAG09O533nbUW5jVL",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "prostatitis-basico",
		name: "Tratamiento Prostatitis (Básico)",
		kind: "treatment",
		livePriceId: "price_1Ovm6FBDAG09O533RkLppuJA",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "prostatitis-completo",
		name: "Tratamiento Prostatitis (Completo)",
		kind: "treatment",
		livePriceId: "price_1Ovm6FBDAG09O5337nbMm3dv",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "sobrepeso-basico",
		name: "Tratamiento Sobrepeso (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlN4BDAG09O5331LhO62Vz",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "sobrepeso-completo",
		name: "Tratamiento Sobrepeso (Completo)",
		kind: "treatment",
		livePriceId: "price_1OvlN4BDAG09O533WfRA6c80",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "vph-basico",
		name: "Tratamiento Virus del Papiloma Humano (Básico)",
		kind: "treatment",
		livePriceId: "price_1OvlylBDAG09O533qWG50hHR",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	},
	{
		catalogId: "vph-completo",
		name: "Tratamiento Virus del Papiloma Humano (Completo)",
		kind: "treatment",
		livePriceId: "price_1OISF7BDAG09O533C8x13uWd",
		testPriceId: TREATMENT_TEST_STRIPE_PRICE_ID
	}
]

const PRICE_TO_NAME = new Map<string, string>()
const PRICE_TO_CATALOG = new Map<string, CatalogPriceEntry>()
const CATALOG_BY_ID = new Map<string, CatalogPriceEntry>()

for (const item of CATALOG_ITEMS) {
	CATALOG_BY_ID.set(item.catalogId, item)
	for (const priceId of [item.livePriceId, item.testPriceId]) {
		PRICE_TO_NAME.set(priceId, item.name)
		PRICE_TO_CATALOG.set(priceId, item)
	}
}

export function getCatalogItemById(catalogId: string): CatalogPriceEntry | undefined {
	return CATALOG_BY_ID.get(catalogId)
}

export function getCatalogItemByPriceId(priceId: string): CatalogPriceEntry | undefined {
	return PRICE_TO_CATALOG.get(priceId)
}

export function getNameByPriceId(priceId: string, fallback = "Producto"): string {
	return PRICE_TO_NAME.get(priceId) || fallback || "Producto"
}

/** Includes `0` for promotions that only grant free shipping. */
export const PROMO_DISCOUNT_PERCENTS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60] as const

export type PromoDiscountPercent = (typeof PROMO_DISCOUNT_PERCENTS)[number]
